import React, { FC, forwardRef, ReactElement, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import filterPropsByComponent from 'ia-react-core/~/utils/components/_filterPropsByComponent';
import Control from '../Control';
import { CheckboxItemControlComposedProps, CheckboxItemControlDefaultProps, CheckboxItemControlValidationProps } from './CheckboxItemControl';

const withCheckboxItemControl = <
  Value = string,
  StateValue = string,
>(WrappedComponent: FC<CheckboxItemControlComposedProps<Value, StateValue>>) => {
  const WithControl = forwardRef<HTMLInputElement, CheckboxItemControlComposedProps<Value, StateValue>>(({
    children, readOnly, textOnly, valueChecked, valueUnchecked, defaultValue = valueUnchecked, required, ...props
  }, ref) => {
    const defaultValidators = useMemo(() => ({
      required: (value: Value, isRequired: boolean) => (
        !isRequired || (
          value === valueChecked
        )
      ),
    }), [valueChecked]);

    const TypedControl = Control<Value, StateValue, HTMLInputElement, CheckboxItemControlValidationProps>;

    return (
      <TypedControl
        {...filterPropsByComponent(props, TypedControl)}
        defaultValue={defaultValue}
        defaultValidators={defaultValidators}
        readOnly={readOnly}
        textOnly={textOnly}
        required={required}
      >
        {({
          id, value, isValid, hasWarning, hasCaution, hasInfo, isTouched, errorMessagesId, onValueChange,
        }) => (
          <WrappedComponent
            id={id}
            data-control
            {...props}
            valueChecked={valueChecked}
            valueUnchecked={valueUnchecked}
            value={value}
            isValid={isValid}
            hasWarning={hasWarning}
            hasCaution={hasCaution}
            hasInfo={hasInfo}
            isTouched={isTouched}
            errorMessagesId={errorMessagesId}
            onValueChange={onValueChange}
            readOnly={readOnly}
            textOnly={textOnly}
            required={required}
            ref={ref}
          >
            {children}
          </WrappedComponent>
        )}
      </TypedControl>
    );
  }) as ((props: CheckboxItemControlComposedProps<Value, StateValue>) => ReactElement<CheckboxItemControlComposedProps<Value, StateValue>>) & {
    defaultProps: Partial<CheckboxItemControlComposedProps<Value, StateValue>>;
  };

  WithControl.defaultProps = CheckboxItemControlDefaultProps;

  return hoistNonReactStatics(WithControl, WrappedComponent);
};

export default withCheckboxItemControl;
